import React from 'react';
import { Spinner } from '../../../controls/Spinner/Spinner';
import { ScrollableBody } from '../../Layout/ScrollableBody';
import { Heading } from '../../../controls/Heading/Heading';
import { CTAButton } from '../../../controls/ButtonBase';
import { GMInfoGrid, GMInfoGridRow, GMInfoGridColumn } from '../../../controls/InfoGrid';
import { useLocale, useLocalization } from '../../../context/Localization/localization';
import { useKioskState } from '../../../context/Kiosk/kiosk';
import { useAppointmentValuation, useAppointmentState } from '../../../context/Appointment/appointment';
import { formatAsCurrency } from '../../../helpers/Number';
import { default as css } from '../index.module.scss';
import { DisplayModel } from '../../../helpers/displayVehicle';
import { GMButtonGroup } from '../../../controls/ButtonGroup';
import { GMRoundedIcon, GMSizedIcon } from '../../../controls/Icon';
import { Locales } from '../../../context/Localization/localization.types';
import { BlackbookAdjustmentMethod } from '../../../API/Enums';
import { GoMotoFunction } from '../../../API/GoMoto.types';

export interface ValuationGridProps {
    onReviewEquity: (review: boolean) => void;
    onSkip?: () => void;
}

export const ValuationGrid = (props: ValuationGridProps & { onSkip: () => void; }) => {
    const locale = useLocale();
    const Localization = useLocalization();
    const appointment = useAppointmentState();
    const { key, dealer } = useKioskState();
    const { valuation, loading } = useAppointmentValuation(key);

    const applyBlackbookAdjustment: GoMotoFunction<[number], number> = (rawValue) => {
        if (!dealer.blackbookAdjustmentMethod || !dealer.blackbookAdjustment) {
            return rawValue;
        }

        let adjustedValue: number;
        if (dealer.blackbookAdjustmentMethod === BlackbookAdjustmentMethod.Percentage) {
            adjustedValue = rawValue * (1 + dealer.blackbookAdjustment / 100);
        }
        else {
            adjustedValue = rawValue + dealer.blackbookAdjustment;
        }

        if (dealer.blackbookMinimumValuation && adjustedValue < dealer.blackbookMinimumValuation) {
            adjustedValue = dealer.blackbookMinimumValuation;
        }

        return Math.max(adjustedValue, 0);
    };

    const { onSkip } = props;
    React.useEffect(() => {
        if (!loading && (!valuation || (!valuation.roughValue && !valuation.averageValue && !valuation.cleanValue))) {
            onSkip();
        }
    }, [loading, onSkip, valuation]);

    if (loading || (!loading && (!valuation || (!valuation.roughValue && !valuation.averageValue && !valuation.cleanValue)))) {
        return <Spinner />;
    }
    else {
        return (
            <ScrollableBody
                heading={
                    <Heading
                        primaryHeading={Localization.YourVehicleValue}
                        secondaryHeading={Localization.formatString(Localization.HereAreYourEstimatedAmounts, {
                            vehicle: <b>{Localization.formatString(locale === Locales.fr ? '{1} {0}' : '{0} {1}', appointment.year ?? '', DisplayModel(appointment))}</b>,
                        })}
                        roundedIcon={
                            <GMRoundedIcon roundedBackgroundSize={'large'} themeType={'secondary'}>
                                <GMSizedIcon
                                    iconType={'Equity'}
                                    iconSize={'extraLarge'}
                                    ariaLabel={Localization.EquityIcon}
                                />
                            </GMRoundedIcon>
                        }
                        hint={Localization.ReviewEquity}
                        showMustache
                    />
                }
                proceedButtons={
                    <GMButtonGroup
                        alignment={'center'}
                        buttons={[
                            (
                                <CTAButton
                                    styleType={'primary'}
                                    onClick={() => props.onReviewEquity(true)}
                                    label={Localization.YesIWould}
                                    kioskOptions={{ square: true }}
                                    id={Localization.YesIWould}
                                    ariaLabel={Localization.YesIWould}
                                />
                            ),
                            (
                                <CTAButton
                                    styleType={'secondary'}
                                    onClick={() => props.onReviewEquity(false)}
                                    label={Localization.NoIWouldNot}
                                    kioskOptions={{
                                        square: true,
                                        squareOptions: {
                                            roundedIcon: (
                                                <GMRoundedIcon
                                                    themeType={'primary'}
                                                    allowInvertedPrimary
                                                    roundedBackgroundSize={'large'}
                                                >
                                                    <GMSizedIcon iconSize={'custom'} iconType={'No'} />
                                                </GMRoundedIcon>
                                            ),
                                        },
                                    }}
                                    id={Localization.NoIWouldNot}
                                    ariaLabel={Localization.NoIWouldNot}
                                />
                            ),
                        ]}
                    />
                }
                unsetButtons
            >
                {valuation && (
                    <div data-testid={'Valuation-Grid'}>
                        <GMInfoGrid>
                            <GMInfoGridRow>
                                <GMInfoGridColumn>
                                    <div className={css.valuationColumn}>
                                        <b>{Localization.Clean}</b>
                                        <div>{formatAsCurrency(applyBlackbookAdjustment(valuation.cleanValue || 0), true)}</div>
                                    </div>
                                </GMInfoGridColumn>
                                <GMInfoGridColumn>
                                    <div className={css.valuationColumn}>
                                        <b>{Localization.Average}</b>
                                        <div>{formatAsCurrency(applyBlackbookAdjustment(valuation.averageValue || 0), true)}</div>
                                    </div>
                                </GMInfoGridColumn>
                                <GMInfoGridColumn>
                                    <div className={css.valuationColumn}>
                                        <b>{Localization.Rough}</b>
                                        <div>{formatAsCurrency(applyBlackbookAdjustment(valuation.roughValue || 0), true)}</div>
                                    </div>
                                </GMInfoGridColumn>
                            </GMInfoGridRow>
                        </GMInfoGrid>
                    </div>
                )}
            </ScrollableBody>
        );
    }
};